import { filterPropertyTypes } from 'boot/globals';
import { Timestamp } from 'firebase/firestore';
import {
  chain,
  cloneDeep,
  filter,
  find,
  get,
  has,
  intersection,
  isArray,
  isEmpty,
  isFunction,
  isNaN,
  isNull,
  isString,
  join,
  map,
  mapValues,
  set,
  size,
  some,
} from 'lodash';
import { mapState } from 'pinia';
import { DATE_FILTER_OPERATORS } from 'src/constants/filters';
import DateRanges from 'src/mixins/DateRanges';
import Time from 'src/mixins/Time';
import { auth } from 'src/store/auth';
import { clients } from 'src/store/clients';
import { dataFields } from 'src/store/dataFields';
import { isMoreThanWeeksAgo } from 'src/utils/FilterUtils';

// The first object wins a duplicate key, and a row lacking the key never matches an object lacking it too.
function buildForeignObjectLookup(foreignObjects, foreignKey) {
  const lookup = new Map();
  Object.values(foreignObjects ?? {}).forEach((foreignObject) => {
    const value = foreignObject?.[foreignKey];
    if (value !== undefined && !lookup.has(value)) {
      lookup.set(value, foreignObject);
    }
  });
  return lookup;
}

export default {

  mixins: [DateRanges, Time],

  computed: {
    ...mapState(auth, ['connectedClinic', 'userRoles']), // @todo remove if not used
    ...mapState(clients, ['clients']), // @todo remove if not used
    ...mapState(dataFields, ['clientDataFields']), // @todo remove if not used
  },

  methods: {

    /**
     * @obsolete For Composition API use {@link formatField} from {@link src/utils/ColumnUtils.js}
     */
    formatCustomDataField(clientDataField, customData) {
      const value = get(customData, clientDataField.id, null);
      switch (clientDataField.type) {
        case 'date': {
          if (value instanceof Timestamp) {
            return this.getLocaleDateString(value);
          }
          return '';
        }
        case 'select': {
          if (isArray(value)) {
            return join(value, ', ');
          }
          return value;
        }
        case 'yesNo': {
          if (typeof value === 'boolean') {
            return value ? this.$t('common.buttonYes') : this.$t('common.buttonNo');
          }
          return this.$t('common.notSelected');
        }
        case 'user': {
          return get(value, 'userName', '');
        }
        default: {
          return value;
        }
      }
    }, // formatCustomDataField()

    getCustomDataFieldFilterType(clientDataField) {
      switch (clientDataField.type) {
        case 'date': {
          return filterPropertyTypes.Date;
        }
        case 'select': {
          return clientDataField.allowMultipleOptions
            ? filterPropertyTypes.ArrayInArray
            : filterPropertyTypes.StringInArray;
        }
        case 'yesNo': {
          return filterPropertyTypes.Boolean;
        }
        case 'user': {
          return filterPropertyTypes.User;
        }
        default: {
          return filterPropertyTypes.StringMatch;
        }
      }
    }, // getCustomDataFieldFilterType()

    getClientDataFieldFilterProperty(clientDataField) {
      if (clientDataField.type === 'user') {
        return `customData.${clientDataField.id}.user`;
      }
      return `customData.${clientDataField.id}`;
    }, // getClientDataFieldFilterProperty()

    getCustomDataFieldFilterProperty(clientDataField) {
      if (clientDataField.type === 'user') {
        return `customData.${clientDataField.id}.user`;
      }
      return `customData.${clientDataField.id}`;
    }, // getCustomDataFieldFilterProperty()

    getCustomDataFieldFilterItems(customDataField) {
      switch (customDataField.type) {
        case 'select': {
          return {
            filterItems: map(customDataField.options, (filterItem) => ({
              value: filterItem,
              label: filterItem,
            })),
          };
        }
        default: {
          return {};
        }
      }
    }, // getCustomDataFieldFilterItems()

    /**
     * @private
     * Used in {@link getFilteredElements}
     * @param {Array.<Object>} elements all items from component
     * @param {Array.<string> | string} filterItem array of parameters to get nested value from the element
     * @param {string} filterCriteriaType type of filter criteria to find matches all over the string
     * or at the beginning or full string match
     * @param {string} filteredItems user input
     * @returns array of items
     */
    filterElementsByStringMatch(elements, filterItem, filterCriteriaType, filteredItems) {
      return filter(elements, (element) => {
        if (!isString(filteredItems)) {
          return false;
        }
        let prop = this.getMatchedParams(filterItem, element);

        if (typeof prop === 'number') {
          prop = String(prop);
        }

        if (prop === null || !isString(prop)) {
          return false;
        }

        if (prop && filterCriteriaType === 'startsWith') {
          return prop.toLowerCase().startsWith(filteredItems.toLowerCase());
        }

        if (prop && filterCriteriaType === 'isEqualTo') {
          return filteredItems.toLowerCase() === prop.toLowerCase();
        }

        if (prop && filterCriteriaType === 'excludes') {
          return !prop.toLowerCase().includes(filteredItems.toLowerCase());
        }

        return prop
          ? prop.toLowerCase().includes(filteredItems.toLowerCase())
          : false;
      });
    }, // filterElementsByStringMatch()

    filterElementsByNumber(elements, filterItem, filterCriteriaType, filteredItems) {
      return filter(
        elements,
        (element) => {
          let prop = this.getMatchedParams(filterItem, element);

          if (prop === null || prop === undefined) {
            return false;
          }
          prop = Number(prop);

          if (isNaN(prop)) {
            return false;
          }

          switch (filterCriteriaType) {
            case 'lessThan':
              return prop < filteredItems;
            case 'lessThanEqual':
              return prop <= filteredItems;
            case 'greaterThanEqual':
              return prop >= filteredItems;
            case 'greaterThan':
              return prop > filteredItems;
            case 'between': {
              const from = Number(get(filteredItems, 'from'));
              const to = Number(get(filteredItems, 'to'));

              if (isNaN(from) || isNaN(to)) {
                return false;
              }

              const min = Math.min(from, to);
              const max = Math.max(from, to);

              return prop >= min && prop <= max;
            }
            default:
              return prop === filteredItems;
          }
        },
      );
    }, // filterElementsByNumber()

    filterElementsByBoolean(elements, filterItem, filteredItems) {
      return filter(
        elements,
        (element) => {
          // extract the actual field value from the element being filtered
          const prop = this.getMatchedParams(filterItem, element);

          // ensure data integrity: only boolean values (true/false) or null are valid
          // for boolean filtering. This prevents invalid filter states from passing through.
          if (typeof filteredItems !== 'boolean' && filteredItems !== null) {
            return false;
          }

          // when user selects "Not Selected" filter option, we need to match records
          // where the Yes/No field was never set (null) or was explicitly cleared (undefined).
          // this allows filtering for incomplete/unset data.
          if (filteredItems === null) {
            return prop === null || prop === undefined;
          }

          // when user selects "Yes" (true) or "No" (false) filter options,
          // match only records with exact boolean values to find explicitly set data.
          return prop === filteredItems;
        },
      );
    }, // filterElementsByBoolean()

    /**
     * @private
     * Used in {@link getFilteredElements}
     *
     * @param {Array.<Object>} elements all items from component
     * @param {Object} filterItem object with main filter info
     * @returns array of items
     */
    filterElementsByDate(elements, filterItem) {
      const statement = get(filterItem, 'filterStatements')[0];
      return filter(elements, (element) => {
        const prop = this.getMatchedParams(filterItem, element);

        if (statement.dateOperator === DATE_FILTER_OPERATORS.IS_MORE_THAN) {
          return isMoreThanWeeksAgo(prop, statement.filteredItems);
        }

        const { fromDate, toDate } = statement;
        const elementTimestamp = get(prop, 'seconds', 0);

        if (statement.filterCriteriaType === 'custom-dates') {
          const dateFromToItems = chain(statement)
            .get('filterDateItems')
            .filter((dateItem) => (dateItem instanceof Timestamp))
            .map(
              (dateItem) => ({
                fromSeconds: dateItem.seconds,
                toSeconds: dateItem.seconds + 86399,
              }),
            )
            .value();
          return some(
            dateFromToItems,
            (dateItem) => (
              (elementTimestamp >= dateItem.fromSeconds)
              && (elementTimestamp <= dateItem.toSeconds)),
          );
        }
        return (elementTimestamp >= fromDate.seconds && elementTimestamp <= toDate.seconds);
      });
    }, // filterElementsByDate()

    /**
     * @private
     * Used in {@link getFilteredElements}
     *
     * @param {Object} filterItem object with main filter info
     * @param {Object} element object from the array to be filtered
     * @returns value of filter property key from the element
     */
    getMatchedParams(filterItem, element) {
      if (filterItem.filterPropertyCallback && isFunction(filterItem.filterPropertyCallback)) {
        return filterItem.filterPropertyCallback(element);
      }

      const matchFilterProperty = filterItem.filterObject
        ? `${filterItem.filterObject}.${filterItem.filterProperty}`
        : filterItem.filterProperty;

      if (filterItem.arrayProperty) {
        return chain(element).get(matchFilterProperty, []).map(filterItem.arrayProperty).value();
      }
      return get(element, matchFilterProperty);
    }, // getMatchedParams()

    getFilteredElementsWithObjects(items, filterObject, foreignObjects = {}) {
      const foreignFilterItems = Object.values(filterObject ?? {}).filter((filterItem) => filterItem?.filterObject);

      if (!foreignFilterItems.length) {
        return this.getFilteredElements(items, filterObject);
      }

      const foreignValueGetters = foreignFilterItems.map((filterItem) => {
        const {
          filterObject: objectName, filterProperty, filterObjectKey, filterObjectForeignKey,
        } = filterItem;
        const lookup = buildForeignObjectLookup(foreignObjects?.[objectName], filterObjectForeignKey);

        return [
          `${objectName}.${filterProperty}`,
          (item) => get(lookup.get(get(item, filterObjectKey)), filterProperty),
        ];
      });

      const extItems = (items ?? []).map((item) => ({
        ...item,
        ...Object.fromEntries(foreignValueGetters.map(([key, getValue]) => [key, getValue(item)])),
      }));
      return this.getFilteredElements(extItems, filterObject);
    }, // getFilteredElementsWithObjects()

    // @depricated Use FilterUtils - getFilteredElements() for Composition API version
    getFilteredElements(items, filterObject) {
      if (isEmpty(filterObject)) {
        return items;
      }
      const cloneFilter = cloneDeep(filterObject);
      let multipleFilterElements = false;

      /**
       * Get items list according to the filters object. Recurcive
       * @param {Array.<Object>} elements all items from component or items as the result of function
       * @param {Object} elementsFilter object with filter key. contains statements for filtering
       * @returns {Array.<Object>} array of items
       */

      const filterElements = (elements = null, elementsFilter = null) => {
        if (isEmpty(elementsFilter)) {
          return elements;
        }

        return chain(elementsFilter)
          .map((filterItem) => {
            const { filterNegative } = filterItem.filterStatements[0] || false;
            switch (filterItem.filterPropertyType) {
              case filterPropertyTypes.NullValue: {
                const criteria = chain(filterItem)
                  .get('filterStatements')
                  .head()
                  .get('filteredItems')
                  .value();

                return filter(
                  elements,
                  (element) => (
                    criteria.includes((this.getMatchedParams(filterItem, element) ?? null) === null)
                  ),
                );
              }
              case filterPropertyTypes.StringMatch: {
                const filterStatement = get(filterItem, 'filterStatements');
                const { filterCriteriaType, filteredItems } = filterStatement[0];

                return this.filterElementsByStringMatch(
                  elements,
                  filterItem,
                  filterCriteriaType,
                  filteredItems,
                );
              }
              case filterPropertyTypes.Duration:
              case filterPropertyTypes.Number: {
                const filterStatement = get(filterItem, 'filterStatements');
                const { filterCriteriaType, filteredItems } = filterStatement[0];
                return this.filterElementsByNumber(
                  elements,
                  filterItem,
                  filterCriteriaType,
                  filteredItems,
                );
              }
              case filterPropertyTypes.Boolean: {
                const filterStatement = get(filterItem, 'filterStatements');
                const { filteredItems } = filterStatement[0];
                return this.filterElementsByBoolean(
                  elements,
                  filterItem,
                  filteredItems,
                );
              }
              case filterPropertyTypes.Client:
              case filterPropertyTypes.User:
              case filterPropertyTypes.StringInArray: {
                const filterStatement = get(filterItem, 'filterStatements');
                return chain(elements).filter((element) => {
                  const getParams = this.getMatchedParams(filterItem, element);
                  return (!!chain(filterStatement[0])
                    .get('filteredItems')
                    .includes(getParams ?? null)
                    .value()
                  ) !== filterNegative;
                }).value();
              }
              case filterPropertyTypes.ArrayContainsUser:
              case filterPropertyTypes.ArrayInArray: {
                // filterStatements contains an array of each filter badge
                const filterStatement = get(filterItem, 'filterStatements');
                const filterResult = chain(elements).filter((element) => {
                  const getParams = this.getMatchedParams(filterItem, element);
                  const filteredItems = get(filterStatement[0], 'filteredItems', []);

                  // in case if filterCriteriaItems contains an array with null value as a criteria and
                  // we need to filter items that have no value (like an empty array in the Clients BU field)
                  if (some(filteredItems, (item) => isNull(item)) && isEmpty(getParams)) {
                    return true;
                  }
                  // filteredItems from filterStatements is an array of all the ids to be search in filter badge
                  // eslint-disable-next-line no-bitwise
                  return !chain(filteredItems)
                    .intersection(getParams)
                    .isEmpty()
                    .value() ^ filterNegative;
                }).value();
                multipleFilterElements = size(filterStatement) > 1;

                /* if there are multiple tag filters on page, take the result of first iteration, remove
                first element from statement and call filterElements() with the result and new filterStatement */
                if (multipleFilterElements) {
                  multipleFilterElements = false;
                  // if need multiple filtering elements except for tags - change the line below
                  cloneFilter.tags.filterStatements = elementsFilter.tags.filterStatements.slice(1);
                  return filterElements(filterResult, cloneFilter);
                }

                return filterResult;
              }
              case filterPropertyTypes.Date: {
                return this.filterElementsByDate(elements, filterItem);
              }
              default:
                return false;
            }
          })
          // compare arrays from result of the function and get unique items from them
          .reduce((prev, curr) => intersection(prev, curr))
          .value();
      };
      return filterElements(items, filterObject);
    }, // getFilteredElements()

    /**
     * Parse filters
     * @param {string} filterJSON stringified filters
     * @param {Array<Object>|null} callbacksRetainSource array of objects containing filters, passed to find and
     * re-apply callbacks to filters that were stringified/parsed
     * @returns {Object}
     */

    jsonParseFilters(filterJSON, callbacksRetainSource = null) {
      let filters = {};
      if (filterJSON) {
        filters = JSON.parse(filterJSON, (key, value) => (
          ((has(value, 'seconds') && has(value, 'nanoseconds')))
            ? new Timestamp(value.seconds, value.nanoseconds)
            : value
        ));
      }

      const result = mapValues(filters, (filterData) => {
        if (filterData.filterPropertyType !== filterPropertyTypes.Date) {
          return filterData;
        }
        const periodFilterData = filterData;
        const preset = find(
          this.periodPresets,
          {
            id: chain(filterData)
              .get(['filterStatements'])
              .head()
              .get('filterCriteriaType')
              .value(),
          },
        );
        if (preset) {
          const fromDate = Timestamp.fromDate(preset.fromTimestamp);
          const toDate = Timestamp.fromDate(preset.toTimestamp);

          set(periodFilterData, ['filterStatements', 0, 'fromDate'], fromDate);
          set(periodFilterData, ['filterStatements', 0, 'toDate'], toDate);
        }

        return periodFilterData;
      });

      // when using JSON.stringify to save filters in local/session storage, properties with type 'function' get omitted
      // this traverses the list of available filters and assigns callbacks back to filters pulled from storage
      if (callbacksRetainSource?.length) {
        const filtersNames = Object.keys(result);
        const filtersWithACallback = callbacksRetainSource.filter((colFilter) => {
          return filtersNames.includes(colFilter?.name) && !!colFilter?.filterPropertyCallback;
        });
        filtersWithACallback.forEach(({ name, filterPropertyCallback }) => {
          result[name].filterPropertyCallback = filterPropertyCallback;
        });
      }

      return result;
    }, // jsonParseFilters()

    /**
     * @deprecated Use FilterUtils - getFilterCriteriaFromColumns() for Composition API version
     * @param {Array.<Object>} columns array of column objects
     * @returns {Array.<Object>} array of filter criteria objects
     */
    getFilterCriteriaFromColumns(columns) {
      return chain(columns)
        .filter('filterable')
        .filter((column) => column.filterable && (this.connectedClinic.integration || !column.requireIntegration))
        .map((column) => ({
          filterType: column.filterType,
          value: column.filterObject
            ? `${column.filterObject}.${column.name}`
            : column.name,
          label: column.selectionLabel || column.label || column.exportLabel || '',
          filterProperty: column.filterProperty || column.field,
          filterPrefix: column.filterPrefix === undefined ? column.label : column.filterPrefix,
          filterObject: column.filterObject || null,
          filterObjectKey: column.filterObjectKey || null,
          filterObjectForeignKey: column.filterObjectForeignKey || null,
          options: {
            filterCriteriaItems: column.filterItems || [],
            arrayProperty: column.filterArrayProperty || null,
            allowNegative: column.filterAllowNegative || false,
            includeFuture: column.filterIncludeFuture || false,
            filterDependency: column.filterDependency || null,
            dateOperators: column.filterDateOperators || false,
            singleValued: column.filterSingleValued || false,
          },
          group: column.group,
          order: column.filterOrder,
          filterPropertyCallback: column.filterPropertyCallback || null,
        }))
        .value();
    }, // getFilterCriteriaFromColumns()

  },
};
